import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft,
  Calendar,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Users,
  DollarSign,
  RefreshCw,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventManagement = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    location: 'Jakkur Aerodrome, Bangalore',
    availableSlots: 10,
    price: 'Contact for pricing',
    bookingInfo: 'Call 9148010687 or WhatsApp to book your slot.',
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/events/all`);
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        // Update existing event
        await axios.put(`${API}/events/${editingEvent.id}`, formData);
        toast.success('Event updated successfully');
      } else {
        // Create new event
        await axios.post(`${API}/events`, formData);
        toast.success('Event created successfully');
      }
      
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      eventDate: new Date(event.eventDate).toISOString().slice(0, 16),
      location: event.location,
      availableSlots: event.availableSlots,
      price: event.price || 'Contact for pricing',
      bookingInfo: event.bookingInfo,
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      eventDate: '',
      location: 'Jakkur Aerodrome, Bangalore',
      availableSlots: 10,
      price: 'Contact for pricing',
      bookingInfo: 'Call 9148010687 or WhatsApp to book your slot.',
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-blue mx-auto mb-4"></div>
          <p className="text-aviation-blue">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm mb-8">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/operations')}
                className="border-aviation-blue text-aviation-blue"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-aviation-blue">
                Event Management
              </h1>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchEvents}
                variant="outline"
                className="border-aviation-blue text-aviation-blue"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
              >
                {showForm ? (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4">
        {/* Event Form */}
        {showForm && (
          <Card className="mb-8 border-l-4 border-aviation-gold">
            <CardHeader>
              <CardTitle className="text-aviation-blue">
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Event Title
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., New Year Special Sky Tour"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Event Date & Time
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.eventDate}
                      onChange={(e) =>
                        setFormData({ ...formData, eventDate: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Location
                    </label>
                    <Input
                      type="text"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Available Slots
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.availableSlots}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          availableSlots: parseInt(e.target.value),
                        })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Description
                    </label>
                    <Textarea
                      placeholder="Describe the event..."
                      rows={3}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Price Information
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., ₹5000 per person"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-aviation-blue">
                      Booking Information
                    </label>
                    <Input
                      type="text"
                      value={formData.bookingInfo}
                      onChange={(e) =>
                        setFormData({ ...formData, bookingInfo: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
                  >
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Events</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {events.length}
                  </p>
                </div>
                <Calendar className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Upcoming Events</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {events.filter((e) => isUpcoming(e.eventDate)).length}
                  </p>
                </div>
                <Calendar className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Slots</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {events.reduce((sum, e) => sum + e.availableSlots, 0)}
                  </p>
                </div>
                <Users className="w-10 h-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {events.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No events found</p>
                <p className="text-gray-400 text-sm mt-2">
                  Create your first event above
                </p>
              </CardContent>
            </Card>
          ) : (
            events.map((event) => (
              <Card
                key={event.id}
                className={`hover:shadow-lg transition-all ${
                  isUpcoming(event.eventDate)
                    ? 'border-l-4 border-green-500'
                    : 'border-l-4 border-gray-300'
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-aviation-blue text-xl mb-2">
                        {event.title}
                      </CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-aviation-gold" />
                          {formatDate(event.eventDate)}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-aviation-gold" />
                          {event.location}
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-aviation-gold" />
                          {event.availableSlots} Slots
                        </div>
                        {event.price && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-aviation-gold" />
                            {event.price}
                          </div>
                        )}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 text-xs rounded-full ${
                        isUpcoming(event.eventDate)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {isUpcoming(event.eventDate) ? 'Upcoming' : 'Past'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{event.description}</p>
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-gray-700">
                      Booking Info:
                    </p>
                    <p className="text-sm text-gray-600">{event.bookingInfo}</p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleEdit(event)}
                      variant="outline"
                      size="sm"
                      className="border-aviation-blue text-aviation-blue hover:bg-aviation-blue hover:text-white"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(event.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EventManagement;
